import { useReactFlow, Node, Edge } from '@xyflow/react'
import { hierarchy, tree } from 'd3-hierarchy'
import { useCallback, useMemo } from 'react'

interface LayoutOptions {
  direction: 'TB' | 'BT' | 'LR' | 'RL' | 'GRID'
  nodeWidth: number
  nodeHeight: number
  spacing: {
    horizontal: number
    vertical: number
  }
  maxNodesPerRow?: number // New option for grid layout
}

const defaultLayoutOptions: LayoutOptions = {
  direction: 'TB', // Top to Bottom
  nodeWidth: 1100,
  nodeHeight: 1100,
  spacing: {
    horizontal: 100, // Increased from 100
    vertical: 100    // Increased from 150
  },
  maxNodesPerRow: 5 // Default to 5 nodes per row to prevent overcrowding
}

// Helper function to ensure a number is finite and not NaN
const safeNumber = (value: number, fallback = 0): number => {
  return Number.isFinite(value) ? value : fallback
}

export function useAutoLayout() {
  const { getNodes, getEdges, setNodes, fitView } = useReactFlow()

  const buildHierarchy = useCallback((nodes: Node[], edges: Edge[]) => {
    // Create adjacency map for edges
    const edgeMap = new Map<string, string[]>()
    const incomingEdges = new Map<string, number>()
    
    // Initialize maps
    nodes.forEach(node => {
      edgeMap.set(node.id, [])
      incomingEdges.set(node.id, 0)
    })
    
    // Build edge relationships
    edges.forEach(edge => {
      const sourceChildren = edgeMap.get(edge.source) || []
      sourceChildren.push(edge.target)
      edgeMap.set(edge.source, sourceChildren)
      
      const targetIncoming = incomingEdges.get(edge.target) || 0
      incomingEdges.set(edge.target, targetIncoming + 1)
    })

    // Find root nodes (nodes with no incoming edges)
    const rootNodes = nodes.filter(node => (incomingEdges.get(node.id) || 0) === 0)
    
    // If no root nodes found, use the first node as root
    if (rootNodes.length === 0 && nodes.length > 0) {
      return { id: nodes[0].id, children: [] }
    }
    
    // Build hierarchy recursively from root
    const buildNode = (nodeId: string, visited = new Set<string>()): any => {
      if (visited.has(nodeId)) return null // Prevent infinite loops
      visited.add(nodeId)
      
      const children = edgeMap.get(nodeId) || []
      return {
        id: nodeId,
        children: children.map(childId => buildNode(childId, visited)).filter(Boolean)
      }
    }

    // Return hierarchy from first root node
    return buildNode(rootNodes[0]?.id || nodes[0]?.id)
  }, [])

  const getNodeDimensions = useCallback((nodeId?: string) => {
    const nodeElement = nodeId 
      ? document.querySelector(`[data-id="${nodeId}"]`)
      : null

    if (nodeElement) {
      const rect = nodeElement.getBoundingClientRect()
      return {
        width: rect.width || defaultLayoutOptions.nodeWidth,
        height: rect.height || defaultLayoutOptions.nodeHeight
      }
    }

    return {
      width: defaultLayoutOptions.nodeWidth,
      height: defaultLayoutOptions.nodeHeight
    }
  }, [])

  const applyGridLayout = useCallback((orientation: 'horizontal' | 'vertical' = 'horizontal') => {
    const nodes = getNodes()
    if (nodes.length === 0) return

    const itemsPerRowOrColumn = 5 // Fixed limit to prevent overcrowding
    
    // Calculate max dimensions from actual node properties
    const maxWidth = Math.max(...nodes.map(node => node.width || defaultLayoutOptions.nodeWidth))
    const maxHeight = Math.max(...nodes.map(node => node.height || defaultLayoutOptions.nodeHeight))
    
    // Use actual node dimensions with spacing padding
    const horizontalSpacing = maxWidth + 50   // Node width + 50px padding
    const verticalSpacing = maxHeight + 50    // Node height + 50px padding
    
    const updatedNodes = nodes.map((node, index) => {
      let x: number, y: number
      
      if (orientation === 'horizontal') {
        // Horizontal grid: 5 items per row, then new row
        const row = Math.floor(index / itemsPerRowOrColumn)
        const col = index % itemsPerRowOrColumn
        
        x = col * horizontalSpacing
        y = row * verticalSpacing
      } else {
        // Vertical grid: 5 items per column, then new column
        const col = Math.floor(index / itemsPerRowOrColumn)
        const row = index % itemsPerRowOrColumn
        
        x = col * horizontalSpacing
        y = row * verticalSpacing
      }
      
      return {
        ...node,
        position: {
          x: safeNumber(x),
          y: safeNumber(y)
        }
      }
    })

    setNodes(updatedNodes)
  }, [getNodes, setNodes])

  // Optimized grid layout: true shelf-packing (largest height first, fill row, then new shelf)
  const applyOptimizedGridLayout = useCallback(() => {
    const nodes = getNodes()
    if (nodes.length === 0) return

    // Step 1: Get safe dimensions for each node and sort by height descending
    const nodeDims = nodes.map((node, idx) => ({
      idx,
      width: Math.max(1, safeNumber(node.width ?? defaultLayoutOptions.nodeWidth, defaultLayoutOptions.nodeWidth)),
      height: Math.max(1, safeNumber(node.height ?? defaultLayoutOptions.nodeHeight, defaultLayoutOptions.nodeHeight)),
    }))
    // Sort by height descending (classic shelf-packing)
    nodeDims.sort((a, b) => b.height - a.height)

    // Step 2: Shelf packing
    const paddingX = 50
    const paddingY = 50
    // Heuristic: try to keep shelves as square as possible
    const totalArea = nodeDims.reduce((sum, n) => sum + n.width * n.height, 0)
    const maxShelfWidth = Math.max(
      Math.ceil(Math.sqrt(totalArea)), // square root of total area
      ...nodeDims.map(n => n.width)
    )

    let shelfY = 0
    let shelfHeight = 0
    let shelfX = 0

    // Store positions by original node index
    const positions: Record<number, { x: number, y: number }> = {}

    for (const n of nodeDims) {
      // If node doesn't fit in current shelf, start new shelf
      if (shelfX + n.width > maxShelfWidth && shelfX > 0) {
        shelfY += shelfHeight + paddingY
        shelfX = 0
        shelfHeight = 0
      }
      positions[n.idx] = { x: shelfX, y: shelfY }
      shelfX += n.width + paddingX
      shelfHeight = Math.max(shelfHeight, n.height)
    }

    // Step 3: Assign positions back to nodes in original order
    const updatedNodes = nodes.map((node, idx) => ({
      ...node,
      position: {
        x: safeNumber(positions[idx].x),
        y: safeNumber(positions[idx].y)
      }
    }))

    setNodes(updatedNodes)
    setTimeout(() => fitView(), 100)
  }, [getNodes, setNodes, fitView])

  const applyTreeLayout = useCallback((direction: 'TB' | 'BT' | 'LR' | 'RL' = 'TB') => {
    const nodes = getNodes()
    const edges = getEdges()
    
    if (nodes.length === 0) return

    // For more than 5 nodes in horizontal layouts, use grid layout instead
    if ((direction === 'LR' || direction === 'RL') && nodes.length > 5) {
      applyGridLayout('horizontal')
      return
    }

    // For more than 10 nodes in vertical layouts, use grid layout instead
    if ((direction === 'TB' || direction === 'BT') && nodes.length > 10) {
      applyGridLayout('vertical')
      return
    }

    // Handle single node case
    if (nodes.length === 1) {
      const updatedNodes = nodes.map(node => ({
        ...node,
        position: { x: 0, y: 0 }
      }))
      setNodes(updatedNodes)
      return
    }

    try {
      // Build hierarchy
      const hierarchyRoot = buildHierarchy(nodes, edges)
      if (!hierarchyRoot) return

      // Create d3 hierarchy
      const root = hierarchy(hierarchyRoot)
      
      // Calculate max dimensions from actual node properties
      const maxWidth = Math.max(...nodes.map(node => node.width || defaultLayoutOptions.nodeWidth))
      const maxHeight = Math.max(...nodes.map(node => node.height || defaultLayoutOptions.nodeHeight))
      
      // Configure tree layout based on direction using actual node dimensions
      const treeLayout = tree()
      
      if (direction === 'TB' || direction === 'BT') {
        // Vertical layout using actual node dimensions with spacing
        treeLayout.size([
          Math.max(400, nodes.length * (maxWidth + 100)),    // Width: node width + 100px spacing
          Math.max(300, root.height * (maxHeight + 150))     // Height: node height + 150px spacing
        ])
      } else {
        // Horizontal layout using actual node dimensions with spacing
        treeLayout.size([
          Math.max(300, root.height * (maxWidth + 150)),     // Width: node width + 150px spacing
          Math.max(400, nodes.length * (maxHeight + 100))    // Height: node height + 100px spacing
        ])
      }

      // Apply layout
      const layoutRoot = treeLayout(root)

      // Create position map
      const positionMap = new Map<string, { x: number; y: number }>();
      
      layoutRoot.descendants().forEach(d => {
        let x = d.x
        let y = d.y
        
        // Adjust coordinates based on direction
        if (direction === 'LR') {
          // Left to Right: swap x and y
          x = d.y
          y = d.x
        } else if (direction === 'RL') {
          // Right to Left: swap and invert x
          x = -d.y
          y = d.x
        } else if (direction === 'BT') {
          // Bottom to Top: invert y
          y = -d.y
        }
        
        positionMap.set(d.data.id, { 
          x: safeNumber(x), 
          y: safeNumber(y) 
        })
      })

      // Update node positions
      const updatedNodes = nodes.map(node => {
        const position = positionMap.get(node.id) || { x: 0, y: 0 }
        return {
          ...node,
          position
        }
      })

      setNodes(updatedNodes)

      // Fit view after layout
      setTimeout(() => fitView(), 100)
      
    } catch (error) {
      console.warn('Tree layout failed, falling back to grid layout:', error)
      applyGridLayout(direction === 'LR' || direction === 'RL' ? 'horizontal' : 'vertical')
    }
  }, [getNodes, getEdges, setNodes, fitView, buildHierarchy, getNodeDimensions, applyGridLayout])

  const layoutActions = useMemo(() => ({
    applyAutoLayout: () => applyTreeLayout('TB'),
    applyHorizontalLayout: () => applyTreeLayout('LR'),
    applyVerticalLayout: () => applyTreeLayout('TB'),
    applyBottomUpLayout: () => applyTreeLayout('BT'),
    applyRightToLeftLayout: () => applyTreeLayout('RL'),
    applyGridLayout: (orientation?: 'horizontal' | 'vertical') => {
      applyGridLayout(orientation)
    },
    applyOptimizedGridLayout, // <-- new optimized grid layout
  }), [applyTreeLayout, applyGridLayout, applyOptimizedGridLayout])

  return layoutActions
}
